import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnChanges,
  output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CheckboxWithLabelComponent,
  EmailValidator,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengMultiselectDirective,
  PrimengPasswordDirective,
  SelectItem,
} from '@web/shared/ui';
import { PasswordModule } from 'primeng/password';
import { MultiSelectModule } from 'primeng/multiselect';
import { AsyncPipe } from '@angular/common';
import { generateRandomPassword, Nillable } from '@web/utils';
import { ButtonModule } from 'primeng/button';
import { Role, RoleProviderService } from '@web/shared/module/auth';
import { UserFormData, UserFormGroup } from '../../models/user-form-data';
import { map, Observable } from 'rxjs';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { isUnavailableRole } from '../../utils/is-unavailable-role';

@Component({
  selector: 'app-user-form',
  imports: [
    ReactiveFormsModule,
    FormControlComponent,
    ButtonModule,
    PrimengButtonDirective,
    PasswordModule,
    PrimengPasswordDirective,
    MultiSelectModule,
    AsyncPipe,
    PrimengMultiselectDirective,
    InputText,
    Checkbox,
    CheckboxWithLabelComponent,
  ],
  templateUrl: './user-form.component.html',
  styleUrl: './user-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserFormComponent implements OnChanges {
  private readonly builder = inject(FormBuilder);
  private readonly rolesProvider = inject(RoleProviderService);

  public readonly data = input<Nillable<UserFormData>>();
  public readonly submitClickEvent = output<UserFormData>();
  public readonly form = this.buildForm();
  public readonly rolesSelectItems$ = this.rolesSelectItemsSource();

  public ngOnChanges(changes: SimpleChanges): void {
    const { data } = changes;

    if (data) {
      this.fillByData();
    }
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  public handleGeneratePasswordClick(): void {
    this.form.controls.password.patchValue(generateRandomPassword());
  }

  public toData(): UserFormData {
    const data = this.form.getRawValue();

    return new UserFormData({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      displayName: data.displayName,
      newsletter: data.newsletter,
      password: data.password,
      roles: data.roles,
      username: data.username,
    });
  }

  public handleClearRolesClick(): void {
    this.clearRolesControl();
  }

  private clearRolesControl(): void {
    this.form.controls.roles.patchValue([]);
    this.form.controls.roles.markAsTouched();
    this.form.controls.roles.markAsDirty();
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue({
      firstName: data?.firstName,
      lastName: data?.lastName,
      displayName: data?.displayName,
      username: data?.username,
      newsletter: data?.newsletter,
      email: data?.email,
      roles: data?.roles,
    });
  }

  private rolesSelectItemsSource(): Observable<SelectItem<Role>[]> {
    return this.rolesProvider
      .selectItems()
      .pipe(
        map((items) => items.filter((item) => !isUnavailableRole(item.value))),
      );
  }

  /* prettier-ignore */
  private buildForm(): UserFormGroup {
    return this.builder.group({
      firstName: this.builder.control<Nillable<string>>(''),
      lastName: this.builder.control<Nillable<string>>(''),
      displayName: this.builder.control<Nillable<string>>(''),
      email: this.builder.nonNullable.control('', [Validators.required, EmailValidator.email]),
      username: this.builder.nonNullable.control('', [Validators.required]),
      roles: this.builder.nonNullable.control<Role[]>([]),
      password: this.builder.control<Nillable<string>>(null),
      newsletter: this.builder.nonNullable.control(false),
    });
  }
}
